import { useEffect } from "react";
import { Minus, Plus, Trash2, ShoppingBag, Lock, Loader2, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCartStore, createShopifyProductFromLocal } from "@/stores/cartStore";
import { getProductById, getProductBySlug, type Product } from "@/data/products";



const CartDrawer = () => {
  const items = useCartStore((s) => s.items);
  const isOpen = useCartStore((s) => s.isOpen);
  const closeCart = useCartStore((s) => s.closeCart);
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const clearCart = useCartStore((s) => s.clearCart);
  const getCheckoutUrl = useCartStore((s) => s.getCheckoutUrl);
  const isLoading = useCartStore((s) => s.isLoading);
  const isSyncing = useCartStore((s) => s.isSyncing);
  const syncCart = useCartStore((s) => s.syncCart);
  const addItem = useCartStore((s) => s.addItem);

  useEffect(() => {
    if (isOpen) syncCart();
  }, [isOpen, syncCart]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + parseFloat(i.price.amount) * i.quantity, 0);

  // Collect related product IDs from cart items, excluding items already in cart
  const cartVariantIds = new Set(items.map((i) => i.variantId));
  const upsellProducts: Product[] = [];
  items.forEach((item) => {
    const handle = item.product.node.handle;
    const prod = getProductBySlug(handle);
    if (prod) {
      prod.relatedProductIds.forEach((rid: number) => {
        const related = getProductById(rid);
        if (related && !cartVariantIds.has(related.shopifyVariantId) && upsellProducts.length < 2 && !upsellProducts.find((u) => u.id === related.id)) {
          upsellProducts.push(related);
        }
      });
    }
  });

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
      closeCart();
    }
  };

  const handleAddUpsell = (product: Product) => {
    const shopifyData = createShopifyProductFromLocal({
      name: product.name,
      tagline: product.tagline,
      price: product.price,
      image: product.images[0],
      slug: product.slug,
      shopifyVariantId: product.shopifyVariantId,
    });
    addItem({ ...shopifyData, quantity: 1 });
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent className="flex flex-col w-full sm:max-w-md p-0">
        <SheetHeader className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-lg font-heading font-bold">
              <ShoppingBag className="w-5 h-5" />
              Tu Carrito ({totalItems})
            </SheetTitle>
          </div>
        </SheetHeader>

        {/* Trust bar */}
        <div className="px-4 py-2.5 bg-secondary border-b border-border">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span>🛡️ COFEPRIS</span>
            <span className="text-border">|</span>
            <span>🏥 Ker Clinic</span>
            <span className="text-border">|</span>
            <span>🔒 Pago Seguro</span>
            <span className="text-border">|</span>
            <span>✓ Garantía 30 días</span>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center">
              <ShoppingBag className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">Tu carrito está vacío</p>
            <Button variant="default" onClick={closeCart} asChild>
              <Link to="/#productos">Explorar Productos</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Free shipping banner */}
            <div className="px-6 pt-4 pb-2">
              <p className="text-sm text-primary font-medium text-center">🚚 ¡Envío GRATIS en todos los pedidos!</p>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((item) => {
                const imageUrl = item.product.node.images?.edges?.[0]?.node?.url;
                const handle = item.product.node.handle;
                const title = item.product.node.title;
                const price = parseFloat(item.price.amount);
                const msiPrice = (price / 6).toFixed(0);

                return (
                  <div key={item.variantId} className="flex gap-4 p-3 rounded-xl border border-border bg-card">
                    <Link
                      to={`/producto/${handle}`}
                      onClick={closeCart}
                      className="w-20 h-20 rounded-lg bg-secondary shrink-0 overflow-hidden"
                    >
                      {imageUrl && (
                        <img src={imageUrl} alt={title} className="w-full h-full object-contain p-1" />
                      )}
                    </Link>

                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/producto/${handle}`}
                        onClick={closeCart}
                        className="text-sm font-semibold text-foreground hover:text-primary transition-colors line-clamp-1"
                      >
                        {title}
                      </Link>
                      <p className="text-sm font-bold text-foreground mt-1">
                        ${price.toLocaleString()} {item.price.currencyCode}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        💳 o ${msiPrice}/mes x 6 MSI
                      </p>

                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                          disabled={isLoading}
                          className="w-7 h-7 rounded-md border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40 transition-colors"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                          disabled={isLoading}
                          className="w-7 h-7 rounded-md border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40 transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeItem(item.variantId)}
                          disabled={isLoading}
                          className="ml-auto w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Payment methods preview */}
              <div className="p-3 rounded-xl bg-secondary/60 border border-border">
                <p className="text-xs font-semibold text-foreground mb-2">💳 Paga como prefieras:</p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2 py-1 rounded border border-border bg-background text-[10px] font-medium text-muted-foreground">VISA</span>
                  <span className="px-2 py-1 rounded border border-border bg-background text-[10px] font-medium text-muted-foreground">MC</span>
                  <span className="px-2 py-1 rounded border border-border bg-background text-[10px] font-medium text-muted-foreground">AMEX</span>
                  <span className="px-2 py-1 rounded border border-border bg-background text-[10px] font-medium text-muted-foreground">🏪 OXXO</span>
                  <span className="px-2 py-1 rounded border border-border bg-background text-[10px] font-medium text-muted-foreground">🏦 SPEI</span>
                  <span className="px-2 py-1 rounded border border-border bg-background text-[10px] font-medium text-muted-foreground">Mercado Pago</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Hasta 12 MSI disponibles · Paga en OXXO · Transferencia SPEI</p>
              </div>

              {/* Upsell section */}
              {upsellProducts.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm font-semibold text-foreground mb-3">Completa tu protocolo:</p>
                  <div className="space-y-2">
                    {upsellProducts.map((product) => (
                      <div key={product.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border bg-card">
                        <Link
                          to={`/producto/${product.slug}`}
                          onClick={closeCart}
                          className="w-14 h-14 rounded-lg bg-secondary shrink-0 overflow-hidden"
                        >
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain p-1" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground truncate">{product.name}</p>
                          <p className="text-xs text-muted-foreground">{product.tagline}</p>
                          <p className="text-xs font-bold text-foreground">${product.price} MXN</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0 text-xs h-8"
                          disabled={isLoading}
                          onClick={() => handleAddUpsell(product)}
                        >
                          + Agregar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-border px-6 py-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="text-lg font-bold text-foreground">${subtotal.toLocaleString()} MXN</span>
              </div>
              <Button
                size="lg"
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                onClick={handleCheckout}
                disabled={isLoading || isSyncing || items.length === 0}
              >
                {isLoading || isSyncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Proceder al Pago Seguro
                  </>
                )}
              </Button>

              {/* Checkout trust text */}
              <p className="text-[11px] text-muted-foreground text-center leading-snug">
                🔒 Checkout seguro con Mercado Pago<br />
                Aceptamos: Tarjetas, OXXO, SPEI, Meses sin intereses
              </p>

              {/* Guarantee mini-banner */}
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-primary/5 border border-primary/10">
                <Shield className="w-4 h-4 text-primary shrink-0" />
                <p className="text-[11px] text-muted-foreground leading-snug">
                  <span className="font-semibold text-foreground">🛡️ Garantía de Satisfacción 30 Días</span><br />
                  Si no estás satisfecho, te devolvemos tu dinero.
                </p>
              </div>

              <button
                onClick={clearCart}
                className="w-full text-xs text-muted-foreground hover:text-destructive transition-colors text-center"
              >
                Vaciar carrito
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
